<template>
  <transition name="modal">
    <div v-if="showContactModal" class="modal-overlay" @click.self="closeContactModal" role="dialog" aria-modal="true" :aria-labelledby="titleId" :aria-describedby="messageId">
      <div class="modal-content-box" ref="modalContent">
        <button @click="closeContactModal" class="close-button" aria-label="Close contact modal">&times;</button>
        <p class="title" :id="titleId">Contact Us</p>
        <p class="message" :id="messageId">Have questions, suggestions, or want to collaborate? We'd love to hear from you!</p>
        
        <form @submit.prevent="submitContactForm" class="flex flex-column">
          <div class="form-group">
            <label class="static-label" for="contact-name">Name</label>
            <input
              id="contact-name"
              :class="['input-field']"
              type="text"
              v-model="contactForm.name"
              @keydown.enter="submitContactForm"
              placeholder="Enter your name"
              ref="firstInput"
            />
          </div>
          
          <div class="form-group">
            <label class="static-label" for="contact-email">Email</label>
            <input
              id="contact-email"
              :class="['input-field']"
              type="email"
              v-model="contactForm.email"
              @keydown.enter="submitContactForm"
              placeholder="Enter your email"
              autocomplete="email"
            />
          </div>
          
          <div class="form-group">
            <label class="static-label" for="contact-subject">Subject</label>
            <input
              id="contact-subject"
              :class="['input-field']"
              type="text"
              v-model="contactForm.subject"
              @keydown.enter="submitContactForm"
              placeholder="Enter subject"
            />
          </div>
          
          <div class="form-group">
            <label class="static-label" for="contact-message">Message *</label>
            <textarea
              id="contact-message"
              :class="['input-field', 'textarea']"
              v-model="contactForm.message"
              placeholder="Enter your message"
              rows="4"
              required
              aria-required="true"
              :aria-invalid="!contactForm.message.trim() && hasSubmitAttempt"
            ></textarea>
          </div>
          
          <button
            class="simple-form-button"
            type="submit"
            :disabled="isSubmitting || !isFormValid"
            @keydown.enter="submitContactForm"
            :aria-label="isSubmitting ? 'Sending message...' : 'Send message to PALLAS team'"
            tabindex="0"
          >
            <span>{{ isSubmitting ? 'Sending...' : 'Send Message' }}</span>
          </button>
          <div v-if="submitMessage" class="submit-message" role="status" aria-live="polite" aria-atomic="true">{{ submitMessage }}</div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { showContactModal, closeContactForm } from '../utils/eventBus';
import emailjs from '@emailjs/browser';

export default {
  name: "ContactModal",
  setup() {
    const contactForm = ref({
      name: '',
      email: '',
      subject: '',
      message: ''
    });

    const isSubmitting = ref(false);
    const submitMessage = ref('');
    const hasSubmitAttempt = ref(false);

    // Modal accessibility
    const modalContent = ref(null);
    const firstInput = ref(null);
    const titleId = `contact-title-${Math.random().toString(36).substr(2, 9)}`;
    const messageId = `contact-message-${Math.random().toString(36).substr(2, 9)}`;
    const previousFocusElement = ref(null);
    
    const isFormValid = computed(() => {
      return contactForm.value.message.trim();
    });
    
    const submitContactForm = async () => {
      hasSubmitAttempt.value = true;

      if (!isFormValid.value) {
        submitMessage.value = 'Please enter a message before sending.';
        return;
      }

      try {
        isSubmitting.value = true;
        submitMessage.value = '';

        const templateParams = {
          title: contactForm.value.subject,
          name: contactForm.value.name,
          time: new Date().toLocaleString(),
          message: contactForm.value.message,
          email: contactForm.value.email
        };

        await emailjs.send(
          'service_1gwh18f',
          'template_jorfw1u',
          templateParams,
          '1L-SCqyOyFX3WYZaa'
        );

        contactForm.value = {
          title: '',
          name: '',
          time: '',
          message: '',
          email: ''
        };

        submitMessage.value = 'Thank you for contacting us! We\'ll get back to you soon.';

        setTimeout(() => {
          closeContactModal();
        }, 2000);

      } catch (error) {
        console.error('Email send failed:', error);
        submitMessage.value = 'Sorry, there was an error sending your message. Please try again.';
      } finally {
        isSubmitting.value = false;
      }
    };

    
    const closeContactModal = () => {
      closeContactForm();
    };

    // Focus management for accessibility
    const setupFocusManagement = () => {
      if (showContactModal.value) {
        previousFocusElement.value = document.activeElement;
        nextTick(() => {
          if (firstInput.value) {
            firstInput.value.focus();
          }
        });
      } else {
        restoreFocus();
      }
    };

    const trapFocus = (event) => {
      if (!modalContent.value) return;

      const focusableElements = modalContent.value.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        closeContactModal();
      } else {
        trapFocus(event);
      }
    };

    const restoreFocus = () => {
      if (previousFocusElement.value) {
        previousFocusElement.value.focus();
        previousFocusElement.value = null;
      }
    };

    // Watch for modal state changes to manage focus
    const unwatchModal = () => {};

    onMounted(() => {
      document.addEventListener('keydown', handleKeydown);
      setupFocusManagement();
    });

    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeydown);
      unwatchModal();
    });

    return {
      contactForm,
      isSubmitting,
      showContactModal,
      isFormValid,
      submitContactForm,
      closeContactModal,
      submitMessage,
      hasSubmitAttempt,
      modalContent,
      firstInput,
      titleId,
      messageId
    };
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.modal-content-box {
  display: flex;
  flex-direction: column;
  gap: var(--paddingStandard);
  max-width: 380px;
  width: 90%;
  padding: var(--paddingSpaceous);
  border-radius: var(--stdRadius);
  position: relative;
  background: var(--bckColor);
  color: var(--textColor);
  box-shadow: var(--stdShadow);
  border: 1px solid var(--borderColor);
  animation: slideUp 0.4s ease;
  max-height: 90vh;
  overflow-y: auto;
}

@media (max-width: 480px) {
  .modal-content-box {
    width: 95%;
    padding: 1rem;
    margin: 1rem;
    max-height: 85vh;
  }
}

.title {
  font-size: var(--subTitle);
  font-weight: 700;
  text-align: center;
  background: var(--art-sci-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  margin-bottom: 4px;
  letter-spacing: 0.02em;
}

.message {
  font-size: var(--mainSubText);
  color: var(--textColor);
  text-align: center;
  line-height: 1.4;
  margin-bottom: 8px;
}

.flex {
  display: flex;
  width: 100%;
  gap: var(--paddingStandard);
}

.flex-column {
  flex-direction: column;
}

.form-group {
  margin-bottom: var(--paddingStandard);
  flex: 1;
}

.static-label {
  display: block;
  font-size: var(--labelText);
  font-weight: 600;
  color: var(--textColor);
  margin-bottom: var(--paddingTight);
}

.input-field {
  background: var(--bckColor);
  border: 1px solid var(--borderColor);
  border-radius: var(--stdRadius);
  padding: 11px 14px;
  width: 100%;
  font-size: 14px;
  font-family: var(--font-primary);
  font-weight: 500;
  color: var(--textColor);
  outline: 0;
  transition: all 0.3s ease;
}

.input-field:focus {
  outline: none;
  border: 1px solid var(--blendColor);
}

.textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.simple-form-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: var(--bckColor);
  border: none;
  border-radius: var(--stdRadius);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  position: relative;
  margin-top: var(--paddingTight);
  font-family: var(--font-primary);
  transition: transform 0.3s ease;
}

.simple-form-button::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(90deg, var(--artColor) 0%, var(--sciColor) 100%);
  border-radius: var(--stdRadius);
  z-index: -1;
}

.simple-form-button::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bckColor);
  border-radius: var(--stdRadius);
  z-index: 0;
}

.simple-form-button span {
  position: relative;
  z-index: 2;
  color: var(--textColor);
}

.simple-form-button:hover:not(:disabled) {
  transform: translateY(-2px);
}

.simple-form-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: var(--neutralColor);
}

.simple-form-button:disabled::before {
  display: none;
}

.simple-form-button:disabled::after {
  background: var(--neutralColor);
}

.close-button {
  position: absolute;
  top: 15px;
  right: 20px;
  background: none;
  border: none;
  font-size: 28px;
  color: var(--textColor);
  cursor: pointer;
  transition: color 0.3s ease;
}

.close-button:hover {
  color: var(--sciColor);
}

/* Accessibility improvements */
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.submit-message {
  margin-top: var(--paddingStandard);
  padding: 0.75rem;
  border-radius: var(--stdRadius);
  font-size: 0.9rem;
  font-weight: 500;
  text-align: center;
  background: rgba(var(--artColor-rgb, 248, 177, 155), 0.1);
  border: 1px solid rgba(var(--artColor-rgb, 248, 177, 155), 0.3);
  color: var(--textColor);
}

/* Mobile form improvements */
@media (max-width: 480px) {
  .input-field {
    font-size: 16px; /* Prevents zoom on iOS */
    padding: 12px 14px;
  }

  .textarea {
    min-height: 80px;
  }

  .form-group {
    margin-bottom: 1rem;
  }

  .simple-form-button {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    min-height: 44px; /* Touch target size */
  }

  .close-button {
    font-size: 24px;
    top: 12px;
    right: 16px;
    min-width: 44px;
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content-box,
.modal-leave-active .modal-content-box {
  transition: all 0.3s ease;
}

.modal-enter-from .modal-content-box {
  transform: scale(0.9);
}

.modal-leave-to .modal-content-box {
  transform: scale(0.9);
}
</style>